import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@/common-utils/libraries/tailwind/utils';

const SearchInputField = React.forwardRef(
    ({ value, onChange, options = [], placeholder = '', className, ...props }, ref) => {
        const [search, setSearch] = useState(value || '');
        const [isOpen, setIsOpen] = useState(false);

        // Sync internal state when value changes
        useEffect(() => {
            setSearch(value || '');
        }, [value]);

        const filteredOptions = useMemo(() => {
            if (!search) { return options; }
            return options.filter((opt) =>
                opt.name.toLowerCase().includes(search.toLowerCase())
            );
        }, [options, search]);

        const baseStyle = cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
            'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-300',
            className
        );

        return (
            <div className='relative w-full'>
                <input
                    type='text'
                    ref={ref}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        onChange?.(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => setTimeout(() => setIsOpen(false), 200)} // small delay to allow click
                    placeholder={placeholder}
                    className={baseStyle}
                    {...props}
                />

                {isOpen && filteredOptions.length > 0 && (
                    <div className='absolute z-50 w-full bg-white border rounded shadow-md max-h-40 overflow-auto'>
                        {filteredOptions.map((opt) => (
                            <div key={opt.value}>
                                <button
                                    className='p-2 hover:bg-gray-100 w-full cursor-pointer'
                                    onMouseDown={() => {
                                        onChange?.(opt.name);
                                        setSearch(opt.name);
                                        setIsOpen(false);
                                    }}
                                >
                                    {opt.name}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
);


SearchInputField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired
        })
    ),
    placeholder: PropTypes.string,
    className: PropTypes.string
};

export default SearchInputField;